<template>
  <b-card no-body class="mb-2">
    <b-card-header class="d-flex align-items-center py-1" style="border-bottom:1px solid #ebe9f1;">
      <feather-icon icon="BarChart2Icon" size="16" class="mr-75 text-primary" />
      <span class="font-weight-bold" style="font-size:0.95rem;">
        {{ viewMode === 'org' ? 'Org Performance' : 'Team Performance' }}
      </span>
      <b-badge variant="light-primary" class="ml-75">{{ teamAnalytics.length }} recruiters</b-badge>
    </b-card-header>

    <div class="table-responsive mb-0 team-table-scroll">
      <table class="table table-hover table-sm mb-0" style="font-size:0.82rem;">
        <thead style="background:#f8f8f8;">
          <tr>
            <th class="px-1 py-75 text-muted font-weight-bold" style="white-space:nowrap;">Recruiter</th>
            <th class="px-1 py-75 text-center text-muted font-weight-bold">Total</th>
            <th class="px-1 py-75 text-center text-muted font-weight-bold">Outbound</th>
            <th class="px-1 py-75 text-center text-muted font-weight-bold">Inbound</th>
            <th class="px-1 py-75 text-center font-weight-bold" style="color:#28c76f;">Connected</th>
            <th class="px-1 py-75 text-center font-weight-bold" style="color:#ff9f43;">Not Ans.</th>
            <th class="px-1 py-75 text-center text-muted font-weight-bold">Failed</th>
            <th class="px-1 py-75 text-muted font-weight-bold" style="min-width:160px;">Connect Rate</th>
            <th class="px-1 py-75 text-muted font-weight-bold" style="white-space:nowrap;">Avg Duration</th>
            <th
              class="px-1 py-75 text-center font-weight-bold"
              style="color:#7367f0; white-space:nowrap;"
            >Profiles Shared</th>
            <th
              class="px-1 py-75 text-muted font-weight-bold"
              style="min-width:140px;"
            >Conversion</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in teamAnalytics" :key="r.empId">
            <!-- Recruiter -->
            <td class="px-1 py-75 align-middle">
              <div class="d-flex align-items-center">
                <b-avatar size="28" :text="r.name ? r.name.charAt(0).toUpperCase() : '?'" variant="light-primary" class="mr-75" />
                <div>
                  <div class="font-weight-bold" style="line-height:1.2;">{{ r.name }}</div>
                  <small class="text-muted">{{ r.empId }}</small>
                </div>
              </div>
            </td>
            <!-- Total -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold">{{ r.total }}</span>
            </td>
            <!-- Outbound -->
            <td class="px-1 py-75 text-center align-middle">
              <b-badge variant="light-primary" class="px-75">
                <feather-icon icon="ArrowUpIcon" size="10" class="mr-25" />{{ r.outbound }}
              </b-badge>
            </td>
            <!-- Inbound -->
            <td class="px-1 py-75 text-center align-middle">
              <b-badge variant="light-info" class="px-75">
                <feather-icon icon="ArrowDownIcon" size="10" class="mr-25" />{{ r.inbound }}
              </b-badge>
            </td>
            <!-- Connected -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold text-success">{{ r.connected }}</span>
            </td>
            <!-- Not Answered -->
            <td class="px-1 py-75 text-center align-middle">
              <span class="font-weight-bold text-warning">{{ r.notAnswered }}</span>
            </td>
            <!-- Failed -->
            <td class="px-1 py-75 text-center align-middle text-danger">{{ r.failed }}</td>
            <!-- Connect Rate -->
            <td class="px-1 py-75 align-middle">
              <div class="d-flex align-items-center" style="gap:8px;">
                <div class="flex-grow-1" style="background:#ebe9f1; border-radius:4px; height:6px; overflow:hidden;">
                  <div
                    :style="{
                      width: r.connectionRate + '%',
                      height: '6px',
                      borderRadius: '4px',
                      background: r.connectionRate >= 60 ? '#28c76f' : r.connectionRate >= 30 ? '#ff9f43' : '#ea5455',
                      transition: 'width 0.4s ease',
                    }"
                  />
                </div>
                <span
                  class="font-weight-bold text-nowrap"
                  :style="{ color: r.connectionRate >= 60 ? '#28c76f' : r.connectionRate >= 30 ? '#ff9f43' : '#ea5455', minWidth:'36px' }"
                >{{ r.connectionRate }}%</span>
              </div>
            </td>
            <!-- Avg Duration -->
            <td class="px-1 py-75 align-middle">
              <span class="text-nowrap font-weight-bold">{{ r.avgDuration }}</span>
            </td>
            <!-- Profiles Shared: unique Called / Not Called -->
            <td class="px-1 py-75 text-center align-middle">
              <div class="d-flex flex-column" style="gap:2px; line-height:1.3;">
                <small class="text-muted" style="font-size:0.7rem;">Called</small>
                <span class="font-weight-bold" style="color:#7367f0;">{{ r.profilesShared || 0 }}</span>
                <small class="text-muted" style="font-size:0.7rem; margin-top:2px;">Not Called</small>
                <span class="font-weight-bold text-danger">{{ r.sharedNotConnected || 0 }}</span>
              </div>
            </td>
            <!-- Conversion Rate -->
            <td class="px-1 py-75 align-middle">
              <div class="d-flex align-items-center" style="gap:8px;">
                <div class="flex-grow-1" style="background:#ebe9f1; border-radius:4px; height:6px; overflow:hidden;">
                  <div
                    :style="{
                      width: r.conversionRate + '%',
                      height: '6px',
                      borderRadius: '4px',
                      background: r.conversionRate >= 50 ? '#7367f0' : r.conversionRate >= 25 ? '#00cfe8' : '#b9b9c3',
                      transition: 'width 0.4s ease',
                    }"
                  />
                </div>
                <span class="font-weight-bold text-nowrap" style="color:#7367f0; min-width:36px;">{{ r.conversionRate }}%</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BBadge, BAvatar } from 'bootstrap-vue'

export default {
  name: 'MyCallsTeamTable',

  components: {
    BCard,
    BCardHeader,
    BBadge,
    BAvatar,
  },

  props: {
    teamAnalytics: { type: Array, required: true },
    viewMode: { type: String, required: true },
  },
}
</script>

<style scoped>
.team-table-scroll {
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;
}

@media (max-width: 576px) {
  .team-table-scroll td,
  .team-table-scroll th {
    white-space: nowrap;
  }
}
</style>
